import { useMemo, useState, type ReactNode } from 'react';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { Tag } from '@/components/common/Tag';
import { cn } from '@/lib/utils';
import { truncate } from '@/lib/format';
import { useWells } from '../lib/api';
import type { WellDto, WellStatus } from '../types';
import { ConfidenceValue, SourceLink, StatusDot, WELL_STATUS_LABELS } from './_shared';

// Tab 3 — Key Wells. The filterable table view of the same payload the map plots.

const selectClass = cn(
  'bg-surface-elevated border border-border rounded-(--radius-sm) px-3 py-2 text-sm min-h-11',
  'font-(family-name:--font-body) focus:outline-2 focus:outline-offset-2 focus:outline-accent'
);

const labelClass = 'text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)';

const operatorName = (w: WellDto): string | null => w.operator?.name ?? w.operatorNameRaw;

export default function KeyWells() {
  const wellsQuery = useWells();
  const wells = useMemo(() => wellsQuery.data?.wells ?? [], [wellsQuery.data]);

  const [region, setRegion] = useState('');
  const [status, setStatus] = useState('');
  const [operator, setOperator] = useState('');
  const [country, setCountry] = useState('');
  const [q, setQ] = useState('');

  const regionOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of wells) if (w.region) m.set(w.region.slug, w.region.name);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [wells]);

  const statusOptions = useMemo(() => {
    const present = new Set(wells.map((w) => w.status));
    return (Object.keys(WELL_STATUS_LABELS) as WellStatus[]).filter((s) => present.has(s));
  }, [wells]);

  const operatorOptions = useMemo(() => {
    const s = new Set<string>();
    for (const w of wells) {
      const n = operatorName(w);
      if (n) s.add(n);
    }
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [wells]);

  const countryOptions = useMemo(() => {
    const s = new Set<string>();
    for (const w of wells) if (w.country) s.add(w.country);
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [wells]);

  if (wellsQuery.isLoading) return <LoadingSkeleton lines={8} />;
  if (wellsQuery.error) return <ErrorState message={(wellsQuery.error as Error).message} />;

  const needle = q.trim().toLowerCase();
  const filtered = wells.filter(
    (w) =>
      (region === '' || w.region?.slug === region) &&
      (status === '' || w.status === status) &&
      (operator === '' || operatorName(w) === operator) &&
      (country === '' || w.country === country) &&
      (needle === '' || w.name.toLowerCase().includes(needle))
  );

  return (
    <div className="stagger">
      <Card>
        <CardTitle>Key Wells</CardTitle>
        <CardDescription className="mt-2">
          High-impact wells to watch in 2026 plus recent regulator-confirmed exploration wellbores.
          Every row carries its source.
        </CardDescription>

        <div className="mt-3 flex flex-wrap items-end gap-x-4 gap-y-3">
          <label className="flex min-w-44 flex-1 flex-col gap-1 sm:flex-none">
            <span className={labelClass}>Search</span>
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Well name…"
              className={selectClass}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className={labelClass}>Region</span>
            <select className={selectClass} value={region} onChange={(e) => setRegion(e.target.value)}>
              <option value="">all</option>
              {regionOptions.map(([slug, name]) => (
                <option key={slug} value={slug}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className={labelClass}>Status</span>
            <select className={selectClass} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">all</option>
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {WELL_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className={labelClass}>Operator</span>
            <select className={selectClass} value={operator} onChange={(e) => setOperator(e.target.value)}>
              <option value="">all</option>
              {operatorOptions.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className={labelClass}>Country</span>
            <select className={selectClass} value={country} onChange={(e) => setCountry(e.target.value)}>
              <option value="">all</option>
              {countryOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Card>

      <Card>
        {filtered.length === 0 ? (
          <CardDescription>No wells match the current filters.</CardDescription>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-accent">
                  <Th>Well</Th>
                  <Th>Country</Th>
                  <Th>Region</Th>
                  <Th>Operator</Th>
                  <Th>Rig</Th>
                  <Th>Status</Th>
                  <Th>Result / volume</Th>
                  <Th center>Source</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((w) => (
                  <WellRow key={w.id} w={w} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-3 text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)">
          {filtered.length} of {wells.length} wells
        </p>
      </Card>
    </div>
  );
}

function Th({ children, center }: { children: ReactNode; center?: boolean }) {
  return (
    <th
      className={cn(
        'py-2 px-3 text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)',
        center ? 'text-center' : 'text-left'
      )}
    >
      {children}
    </th>
  );
}

function WellRow({ w }: { w: WellDto }) {
  return (
    <tr className="border-b border-border align-top hover:bg-surface-hover">
      <td className="py-3 px-3">
        <span className="font-medium text-text">{w.name}</span>
        {w.isHighImpact && (
          <Tag variant="tier-2" className="ml-2" title="Westwood high-impact well 2026">
            High impact
          </Tag>
        )}
      </td>
      <td className="py-3 px-3 whitespace-nowrap">{w.country ?? '—'}</td>
      <td className="py-3 px-3 whitespace-nowrap">{w.region?.name ?? '—'}</td>
      <td className="py-3 px-3">{operatorName(w) ?? '—'}</td>
      <td className="py-3 px-3">{w.rig?.name ?? w.rigNameRaw ?? '—'}</td>
      <td className="py-3 px-3 whitespace-nowrap">
        <span className="inline-flex items-center gap-1.5" title={w.statusRaw ?? undefined}>
          <StatusDot status={w.status} />
          {WELL_STATUS_LABELS[w.status]}
        </span>
      </td>
      <td className="py-3 px-3 max-w-96">
        {w.result ? (
          <ConfidenceValue value={w.result.summary} confidence={w.result.confidence} />
        ) : w.statusRaw ? (
          <span className="text-muted" title={w.statusRaw}>
            {truncate(w.statusRaw, 90)}
          </span>
        ) : (
          '—'
        )}
      </td>
      <td className="py-3 px-3 text-center">
        <SourceLink source={w.source} />
      </td>
    </tr>
  );
}
